/**
 * MotivoSection - Selector de motivos + extensión de período
 * Sub-componente presentacional extraído de DayEditModal
 */
import React from 'react';
import { Check, Award, ChevronDown, ChevronUp, Calendar as CalendarIcon, Trash2 } from 'lucide-react';

export default function MotivoSection({
    showMotivoSection,
    setShowMotivoSection,
    motivos,
    selectedMotivo,
    setSelectedMotivo,
    extendEnabled,
    setExtendEnabled,
    extendEndDate,
    setExtendEndDate,
    dayData,
    hsSaldo
}) {
    return (
        <div className="border rounded-2xl overflow-hidden transition-colors" style={{ borderColor: 'rgba(239, 68, 68, 0.2)' }}>
            <button
                id="tour-edit-motivo"
                onClick={() => setShowMotivoSection(!showMotivoSection)}
                className="w-full flex items-center justify-between p-3 bg-red-500/10 hover:bg-red-500/20 transition-colors border-b"
                style={{ borderColor: 'rgba(239, 68, 68, 0.1)' }}
            >
                <span className={`flex items-center gap-2 font-bold ${showMotivoSection ? 'text-red-600 dark:text-red-400' : 'text-[var(--text-muted)]'}`}>
                    <Award className="w-4 h-4" />
                    Motivo (Asamblea, baja... )
                    {selectedMotivo && (
                        <span className="text-[10px] bg-red-500/15 text-red-700 dark:text-red-400 px-2.5 py-1 rounded-full border border-red-500/20 uppercase font-black">
                            {motivos.find(m => m.id === selectedMotivo)?.nombre || 'Seleccionado'}
                        </span>
                    )}
                </span>
                {showMotivoSection ? <ChevronUp className="w-4 h-4 text-red-400" /> : <ChevronDown className="w-4 h-4 text-red-400" />}
            </button>

            {showMotivoSection && (
                <div className="p-3 space-y-2">
                    {motivos.length === 0 ? (
                        <p className="text-[var(--text-secondary)] text-sm py-4 text-center">
                            No hay motivos disponibles
                        </p>
                    ) : (
                        motivos.map((motivo) => {
                            const isHS = motivo.id === 19;
                            const hasHSSaldo = hsSaldo && hsSaldo.saldo >= (hsSaldo.config?.horas_por_jornada || 8);
                            const isDisabled = isHS && !hasHSSaldo;

                            return (
                                <button
                                    key={motivo.id}
                                    onClick={() => !isDisabled && setSelectedMotivo(motivo.id)}
                                    disabled={isDisabled}
                                    className={`w-full flex items-center gap-4 p-4 rounded-2xl transition-all text-left border shadow-sm ${selectedMotivo === motivo.id
                                        ? 'ring-2 ring-cyan-400 bg-cyan-500/10 border-cyan-500/40'
                                        : isDisabled
                                            ? 'opacity-40 grayscale cursor-not-allowed bg-black/10 border-transparent'
                                            : 'border-white/5 hover:border-white/20'
                                        }`}
                                    style={{ 
                                        background: selectedMotivo === motivo.id ? 'rgba(34, 211, 238, 0.1)' : 'var(--bg-input)'
                                    }}
                                >
                                    <div
                                        className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center relative"
                                        style={{ backgroundColor: motivo.color }}
                                    >
                                        {isDisabled && (
                                            <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg">
                                                <span className="text-[10px] font-bold text-white">X</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between gap-2">
                                            <p className="font-bold truncate" style={{ color: isDisabled ? 'var(--text-muted)' : 'var(--text-primary)' }}>
                                                {motivo.nombre}
                                            </p>
                                            {isHS && hsSaldo && (
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${hasHSSaldo ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                                    {hsSaldo.saldo}h / {hsSaldo.config?.horas_por_jornada || 8}h
                                                </span>
                                            )}
                                        </div>
                                        {motivo.descripcion && (
                                            <p className="text-[10px] font-medium leading-tight mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                                                {isDisabled ? `Necesitas mín. ${hsSaldo?.config?.horas_por_jornada || 8}h (tienes ${hsSaldo?.saldo || 0}h)` : motivo.descripcion}
                                            </p>
                                        )}
                                    </div>
                                    {selectedMotivo === motivo.id && (
                                        <Check className="w-5 h-5 text-cyan-400 shrink-0" />
                                    )}
                                </button>
                            );
                        })
                    )}

                    {/* Extend to date range option */}
                    {selectedMotivo && (
                        <div className="mt-3 pt-3 border-t border-white/10">
                            <div className="flex items-center justify-between">
                                <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                                    <CalendarIcon className="w-4 h-4 text-orange-400" />
                                    Extender hasta otra fecha
                                </label>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setExtendEnabled(!extendEnabled);
                                        if (!extendEnabled) {
                                            const nextWeek = new Date(dayData.dateStr);
                                            nextWeek.setDate(nextWeek.getDate() + 7);
                                            setExtendEndDate(nextWeek.toISOString().split('T')[0]);
                                        }
                                    }}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${extendEnabled ? 'bg-orange-500' : 'bg-gray-600'
                                        }`}
                                >
                                    <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${extendEnabled ? 'translate-x-6' : 'translate-x-1'
                                            }`}
                                    />
                                </button>
                            </div>

                            {extendEnabled && (
                                <div className="mt-3 space-y-2">
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm text-[var(--text-muted)]">Hasta:</span>
                                        <input
                                            type="date"
                                            value={extendEndDate}
                                            min={dayData.dateStr}
                                            onChange={(e) => setExtendEndDate(e.target.value)}
                                            className="flex-1 px-3 py-2 rounded-lg bg-orange-500/10 border border-orange-500/30 text-orange-400 text-sm focus:outline-none focus:border-orange-500"
                                        />
                                    </div>
                                    {extendEndDate && extendEndDate >= dayData.dateStr && (
                                        <p className="text-xs text-orange-400 flex items-center gap-1">
                                            <CalendarIcon className="w-3 h-3" />
                                            Se marcarán {Math.ceil((new Date(extendEndDate) - new Date(dayData.dateStr)) / (1000 * 60 * 60 * 24)) + 1} días
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    
                    {/* Botón para quitar el motivo si ya hay uno seleccionado */}
                    {selectedMotivo && (
                        <div className="mt-4 pt-4 border-t border-white/5">
                            <button
                                onClick={() => {
                                    setSelectedMotivo(null);
                                    if (setExtendEnabled) setExtendEnabled(false);
                                    if (setExtendEndDate) setExtendEndDate('');
                                }}
                                className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-red-500/10 text-red-600 dark:text-red-400 font-black text-[10px] uppercase tracking-widest hover:bg-red-500/15 border border-red-500/20 transition-all shadow-sm"
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                                Quitar este motivo
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
